/**
 * What a purchase checkout's renderer draws, web and native alike
 * (LIS-1173, the purchase design's §2.6): the journey's state, and the
 * status its primary button tells, in the attempt view's own words, on the
 * checkout core's beats (`createStepTimeline`: each step held long enough
 * to read, the fold a beat after the press), with the view's note and
 * actions once the status has caught up with it.
 */
import { createStepTimeline } from "@libs/checkout-status";
import type { Clock, PayPhase, StatusLine } from "@libs/checkout-status";
import type { AttemptAction, AttemptView } from "@libs/platform-wire/purchase";
import type { PurchaseJourney, PurchaseJourneyState } from "./journey";
import { attemptOpen, type AttemptRead } from "./transport";

export type PurchaseSnapshot = {
  journey: PurchaseJourneyState;
  /** The attempt's view, read (null: the form is up). */
  view: AttemptView | null;
  /** What the button says now; null while it is the form's own button. */
  line: StatusLine | null;
  /** Where the press has got to: from `folding`, all but the status folds. */
  phase: PayPhase | null;
  /** Under the status: the view's note and what the buyer can do, shown
   *  once the status says what the view does. */
  outcome: { note: string | null; actions: AttemptAction[] } | null;
  /** Something is moving that the buyer may not walk away from: a submit,
   *  a start not yet found or refused, or an attempt not yet ended, unless
   *  it is `confirming` (nothing more is asked of the buyer, who may leave
   *  unasked while the seller's answer is confirmed). */
  holding: boolean;
};

/** Said where the checkout would be when the purchase as read now (the
 *  buyer's own read, or another delivery country's) no longer holds what
 *  they chose. */
export const ORDER_CHANGED = "This has changed since you chose it. Go back to choose again.";

/** A free order's button, in place of "Continue · £…". */
export const freeOrderWords = (lines: readonly { quantity: number }[]) =>
  lines.reduce((sum, line) => sum + line.quantity, 0) > 1 ? "Get tickets" : "Get ticket";

/** What the button says while a press waits for the attempt's first view. */
export const STARTING = "Getting started";

/** A status as one step of the timeline: its words and mark. */
const stepOf = (line: StatusLine) => JSON.stringify([line.words, line.mark]);
const lineOf = (step: string): StatusLine => {
  const [words, mark] = JSON.parse(step) as [string, StatusLine["mark"]];
  return { words, mark };
};

export function createPurchasePresenter(
  journey: PurchaseJourney,
  clock: Clock,
  options: {
    /** The status has caught up with an attempt come to rest: its outcome
     *  is on the button. */
    onShown?: (attempt: AttemptRead) => void;
  } = {},
) {
  let shown: { step: string | null; phase: PayPhase | null } = { step: null, phase: null };
  let snapshot: PurchaseSnapshot | null = null;
  const listeners = new Set<() => void>();
  const target = (state: PurchaseJourneyState): string | null =>
    state.attempt?.view
      ? stepOf(state.attempt.view.status)
      : state.attempt || state.submitting
        ? stepOf({ words: STARTING, mark: "spinner" })
        : null;
  const changed = () => {
    snapshot = null;
    const { attempt } = journey.snapshot();
    if (options.onShown && attempt?.view && shown.step === stepOf(attempt.view.status)) options.onShown(attempt);
    for (const fn of listeners) fn();
  };
  const timeline = createStepTimeline<string>({
    clock,
    onChange: (view) => {
      shown = view;
      changed();
    },
  });
  const read = (): PurchaseSnapshot => {
    const state = journey.snapshot();
    const view = state.attempt?.view ?? null;
    const caughtUp = !!view && shown.step === stepOf(view.status);
    return {
      journey: state,
      view,
      line: shown.step === null ? null : lineOf(shown.step),
      phase: shown.phase,
      outcome:
        caughtUp && view && (view.note !== null || view.actions.length > 0)
          ? { note: view.note, actions: view.actions }
          : null,
      holding:
        state.submitting ||
        state.pendingStart !== null ||
        (attemptOpen(state.attempt) && state.attempt?.view?.lifecycle !== "confirming"),
    };
  };
  let unsubscribe: (() => void) | null = null;
  return {
    getSnapshot: () => (snapshot ??= read()),
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    /** Mounted: follow the journey, and let the status's beats run. */
    start() {
      if (unsubscribe) return;
      unsubscribe = journey.subscribe(() => {
        timeline.setTarget(target(journey.snapshot()));
        changed();
      });
      timeline.setTarget(target(journey.snapshot()));
      timeline.resume();
      changed();
    },
    /** Unmounted: nothing fires until `start` again. */
    stop() {
      unsubscribe?.();
      unsubscribe = null;
      timeline.pause();
    },
    dispose() {
      unsubscribe?.();
      unsubscribe = null;
      timeline.dispose();
      listeners.clear();
    },
  };
}
export type PurchasePresenter = ReturnType<typeof createPurchasePresenter>;
